import { useQuery } from "@tanstack/react-query";

import { useScopeParams } from "./useScopeParams";
import { useFeatureFlag } from "./useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import discrepancyMarkersApi, {
  type MarkerLevel,
  type MarkerMap,
} from "@/services/discrepancyMarkersApi";

/**
 * One marker lookup per trial, shared by every screen that shows records.
 *
 * The map is small (a few hundred keys on the largest demo trial) and cached
 * under one query key, so a screen costs nothing extra to decorate and
 * resolving a discrepancy updates every marker on the next refetch. When the
 * feature flag is ungranted — or the backend's master switch is off — the map
 * is simply empty and callers render exactly as they did before markers.
 */
export function useDiscrepancyMarkers(level: MarkerLevel = "subject") {
  const { id, scope } = useScopeParams();
  const enabled = useFeatureFlag(FEATURE_FLAGS.DISCREPANCY_MARKERS);

  const query = useQuery({
    queryKey: ["discrepancy-markers", id, level],
    queryFn: () => discrepancyMarkersApi.getMarkers(id, level),
    enabled: enabled && scope === "trial" && Boolean(id),
    staleTime: 60_000,
  });

  const markers: MarkerMap = query.data?.markers ?? {};

  return {
    markers,
    isEnabled: enabled && (query.data?.enabled ?? false),
    isLoading: query.isLoading,
    lookup: (key: string | null | undefined) =>
      key ? (markers[key] ?? null) : null,
  };
}

/** Key for a visit-level marker; mirrors the backend's `subject|visit`. */
export function visitMarkerKey(
  subject: string | null | undefined,
  visit: string | null | undefined,
): string | null {
  if (!subject || !visit) return null;
  return `${subject}|${visit}`;
}

export function useRecordDiscrepancyDetail(entityId?: string) {
  const { id, scope } = useScopeParams();
  const enabled = useFeatureFlag(FEATURE_FLAGS.DISCREPANCY_MARKERS);

  const query = useQuery({
    queryKey: ["discrepancy-markers-record", id, entityId],
    queryFn: () => discrepancyMarkersApi.getRecordDetail(id, entityId!),
    enabled: enabled && scope === "trial" && Boolean(id) && Boolean(entityId),
    staleTime: 60_000,
  });

  return { summary: query.data, isLoading: query.isLoading };
}

/** Row fields a record marker will try, in order, against the record map. */
export const RECORD_ID_FIELDS = [
  "adverse_event_id",
  "lab_id",
  "assessment_id",
  "imaging_id",
  "participant_questionnaire_id",
  "concomitant_medication_id",
  "trial_visit_id",
  "protocol_deviation_id",
];

export function recordIdFor(
  record: Record<string, unknown> | undefined,
): string | null {
  if (!record) return null;
  for (const field of RECORD_ID_FIELDS) {
    const value = record[field];
    if (value !== null && value !== undefined && value !== "") {
      return String(value);
    }
  }
  return null;
}

export function useSubjectDiscrepancySummary(subjectId?: string) {
  const { id, scope } = useScopeParams();
  const enabled = useFeatureFlag(FEATURE_FLAGS.DISCREPANCY_MARKERS);

  const query = useQuery({
    queryKey: ["discrepancy-markers-subject", id, subjectId],
    queryFn: () => discrepancyMarkersApi.getSubjectSummary(id, subjectId!),
    enabled: enabled && scope === "trial" && Boolean(id) && Boolean(subjectId),
    staleTime: 60_000,
  });

  return {
    summary: query.data,
    isEnabled: enabled && (query.data?.enabled ?? false),
    isLoading: query.isLoading,
  };
}
